<template>
  <v-container fluid>
    <!-- Point Totals -->
    <v-row class="mt-2">
      <v-col cols="12" sm="6" md="3" v-for="pointType in pointTypes" :key="pointType.id">
        <v-card outlined class="pa-3 text-center">
          <div class="text-subtitle-2">{{ pointType.name }}</div>
          <div class="text-h5">{{ Math.round(displayBank(pointType)) }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Week navigation -->
    <v-row class="mt-6 align-center" no-gutters>
      <v-col cols="auto">
        <v-btn icon @click="shiftWeek(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
      </v-col>
      <v-col class="text-center">
        <span class="text-body-2 text-sm-subtitle-1">{{ weekLabel }}</span>
        <v-btn small text class="ml-2" v-if="!isCurrentWeek" @click="goToCurrentWeek()"
          >Today</v-btn
        >
      </v-col>
      <v-col cols="auto">
        <v-btn icon @click="shiftWeek(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
      </v-col>
    </v-row>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mt-4"></v-progress-linear>

    <!-- Tracker Table -->
    <div
      class="text-caption grey--text d-flex align-center justify-center mt-4 mb-n1 d-sm-none"
      v-if="!loading && habits.length">
      <v-icon x-small color="grey" class="mr-1">mdi-gesture-swipe-horizontal</v-icon>
      Swipe to see all days
    </div>
    <div class="tracker-frozen-row elevation-1">
      <!-- Frozen habit-name column. Kept as its own table entirely outside the
           horizontally-scrolling element below, rather than a `position: sticky`
           column inside it, since sticky table cells don't play nicely with
           programmatically setting scrollLeft on the scrolling ancestor. -->
      <v-simple-table class="tracker-frozen-table">
        <template v-slot:default>
          <thead>
            <tr class="tracker-header-row">
              <th class="tracker-frozen-cell">Habit</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="habit in habits" :key="habit.id" class="tracker-body-row">
              <td class="tracker-frozen-cell">
                <div class="tracker-frozen-line">{{ habit.name }}</div>
                <div class="text-caption grey--text tracker-frozen-line">
                  +{{ habit.point_reward }} {{ pointTypeName(habit.point_type) }}
                </div>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <!-- Scrollable day columns only - the frozen column above is not part of this
           scrolling element, so scrollWidth/scrollLeft reflect only the days. -->
      <div class="tracker-scroll-wrapper" ref="scrollWrapper">
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr class="tracker-header-row">
                <th v-for="day in weekDays" :key="day.iso" class="text-center tracker-day-col">
                  <div>
                    <span class="d-none d-sm-inline">{{ day.label }}</span>
                    <span class="d-sm-none">{{ day.label.charAt(0) }}</span>
                  </div>
                  <div
                    class="text-caption"
                    :class="{ 'primary--text font-weight-bold': day.isToday }">
                    {{ day.dateLabel }}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="habit in habits" :key="habit.id" class="tracker-body-row">
                <td
                  v-for="day in weekDays"
                  :key="day.iso"
                  class="text-center tracker-cell tracker-day-col">
                  <v-tooltip top v-if="!findHabitDay(habit, day)">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-btn icon disabled>
                          <v-icon color="grey lighten-3">mdi-minus-circle-outline</v-icon>
                        </v-btn>
                      </span>
                    </template>
                    <span>Generate days for this habit on the Habits page first</span>
                  </v-tooltip>
                  <v-btn
                    v-else
                    icon
                    :loading="togglingKey === cellKey(habit, day)"
                    :aria-label="`Toggle ${habit.name} for ${day.dateLabel}`"
                    @click="toggle(habit, day)">
                    <v-icon :color="isCompleted(habit, day) ? 'good' : 'grey lighten-1'">
                      {{
                        isCompleted(habit, day)
                          ? "mdi-check-circle"
                          : "mdi-checkbox-blank-circle-outline"
                      }}
                    </v-icon>
                  </v-btn>

                  <!-- Floating point popups -->
                  <div
                    v-for="popup in popupsFor(habit, day)"
                    :key="popup.key"
                    class="point-popup good--text">
                    +{{ popup.amount }}
                  </div>

                  <!-- Floating streak bonus badges -->
                  <div
                    v-for="badge in bonusBadgesFor(habit, day)"
                    :key="badge.key"
                    class="bonus-badge">
                    <v-icon color="amber darken-2" class="bonus-badge-icon">
                      {{ badge.type === "month" ? "mdi-crown" : "mdi-trophy" }}
                    </v-icon>
                    <div class="bonus-badge-text">{{ badge.label }}</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </div>

    <v-alert v-if="!loading && habits.length === 0" type="info" outlined class="mt-4">
      No habits yet. Add one on the Habits page first.
    </v-alert>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      habits: [],
      pointTypes: [],
      habitDays: [],
      loading: true,
      weekStart: this.startOfWeek(new Date()),
      togglingKey: null,
      animatedBanks: {},
      popups: [],
      bonusBadges: [],
    }
  },
  computed: {
    weekDays() {
      let days = []
      let dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
      let today = this.formatIso(new Date())

      for (let i = 0; i < 7; i++) {
        let date = new Date(this.weekStart)
        date.setDate(date.getDate() + i)
        let iso = this.formatIso(date)

        days.push({
          date,
          iso,
          label: dayNames[date.getDay()],
          dateLabel: `${date.getMonth() + 1}/${date.getDate()}`,
          isToday: iso === today,
        })
      }

      return days
    },
    weekLabel() {
      let start = this.weekDays[0].date
      let end = this.weekDays[6].date
      let opts = { month: "short", day: "numeric" }
      return `${start.toLocaleDateString(undefined, opts)} - ${end.toLocaleDateString(
        undefined,
        opts
      )}, ${end.getFullYear()}`
    },
    isCurrentWeek() {
      return this.formatIso(this.weekStart) === this.formatIso(this.startOfWeek(new Date()))
    },
  },
  methods: {
    startOfWeek(date) {
      let d = new Date(date)
      d.setHours(0, 0, 0, 0)
      d.setDate(d.getDate() - d.getDay())
      return d
    },
    formatIso(date) {
      let year = date.getFullYear()
      let month = String(date.getMonth() + 1).padStart(2, "0")
      let day = String(date.getDate()).padStart(2, "0")
      return `${year}-${month}-${day}`
    },
    pointTypeName(id) {
      let pointType = this.pointTypes.find((p) => p.id === id)
      return pointType ? pointType.name : ""
    },
    displayBank(pointType) {
      return this.animatedBanks[pointType.id] !== undefined
        ? this.animatedBanks[pointType.id]
        : pointType.bank
    },
    cellKey(habit, day) {
      return `${habit.id}_${day.iso}`
    },
    findHabitDay(habit, day) {
      return this.habitDays.find((d) => d.habit === habit.id && d.date === day.iso)
    },
    isCompleted(habit, day) {
      let habitDay = this.findHabitDay(habit, day)
      return habitDay ? habitDay.completed : false
    },
    popupsFor(habit, day) {
      return this.popups.filter((p) => p.habitId === habit.id && p.dateIso === day.iso)
    },
    bonusBadgesFor(habit, day) {
      return this.bonusBadges.filter((b) => b.habitId === habit.id && b.dateIso === day.iso)
    },
    async loadPointTypes() {
      await this.$django_api.get("habits/point_type/").then((response) => {
        this.pointTypes = response.data
        for (let pointType of this.pointTypes) {
          this.$set(this.animatedBanks, pointType.id, pointType.bank)
        }
      })
    },
    async loadHabits() {
      await this.$django_api.get("habits/habit/").then((response) => {
        this.habits = response.data
      })
    },
    async loadHabitDays() {
      this.loading = true
      let start = this.formatIso(this.weekDays[0].date)
      let end = this.formatIso(this.weekDays[6].date)

      await this.$django_api.get(`habits/habit_day/?start=${start}&end=${end}`).then((response) => {
        this.habitDays = response.data
      })
      this.loading = false

      this.$nextTick(() => {
        this.scrollToLatestDayIfNeeded()
      })
    },
    scrollToLatestDayIfNeeded() {
      // On mobile, once the week is more than half over, default the horizontally
      // scrolling tracker table to show the later days instead of Sunday.
      if (!this.isCurrentWeek) return
      if (!this.$vuetify.breakpoint.xsOnly) return
      if (new Date().getDay() < 3) return

      requestAnimationFrame(() => {
        let wrapper = this.$refs.scrollWrapper
        if (wrapper) {
          wrapper.scrollLeft = wrapper.scrollWidth
        }
      })
    },
    async shiftWeek(delta) {
      let newStart = new Date(this.weekStart)
      newStart.setDate(newStart.getDate() + delta * 7)
      this.weekStart = newStart
      await this.loadHabitDays()
    },
    async goToCurrentWeek() {
      this.weekStart = this.startOfWeek(new Date())
      await this.loadHabitDays()
    },
    async toggle(habit, day) {
      let habitDay = this.findHabitDay(habit, day)
      if (!habitDay) return

      let newCompleted = !habitDay.completed
      let key = this.cellKey(habit, day)
      this.togglingKey = key

      await this.$django_api
        .put("habits/habit_day/", { id: habitDay.id, completed: newCompleted })
        .then((response) => {
          console.log(response)
          Object.assign(habitDay, response.data)

          let pointType = this.pointTypes.find((p) => p.id === habit.point_type)
          if (pointType && response.data.point_type) {
            let oldBank = this.displayBank(pointType)
            pointType.bank = response.data.point_type.bank
            this.animateBank(pointType.id, oldBank, pointType.bank)

            if (newCompleted) {
              this.showPointPopup(habit, day, habit.point_reward)
            }
          }

          if (Array.isArray(response.data.bonuses)) {
            for (let bonus of response.data.bonuses) {
              this.showBonusBadge(habit, day, bonus)
            }
          }
        })

      this.togglingKey = null
    },
    showPointPopup(habit, day, amount) {
      let key = `${habit.id}_${day.iso}_${Date.now()}`
      this.popups.push({ key, habitId: habit.id, dateIso: day.iso, amount })

      setTimeout(() => {
        this.popups = this.popups.filter((p) => p.key !== key)
      }, 900)
    },
    showBonusBadge(habit, day, bonus) {
      let key = `${habit.id}_${day.iso}_${bonus.type}_${Date.now()}`
      let isMonth = bonus.type === "month"
      let label = `Perfect ${isMonth ? "Month" : "Week"}! +${bonus.amount}`

      this.bonusBadges.push({ key, habitId: habit.id, dateIso: day.iso, type: bonus.type, label })

      setTimeout(() => {
        this.bonusBadges = this.bonusBadges.filter((b) => b.key !== key)
      }, 2200)

      // this.$store.dispatch("set_alert", {
      //   type: "success",
      //   message: `${label} ${this.pointTypeName(habit.point_type)}`,
      // })
    },
    animateBank(id, from, to) {
      if (from === to) {
        this.$set(this.animatedBanks, id, to)
        return
      }

      let duration = 500
      let startTime = null
      let step = (timestamp) => {
        if (!startTime) startTime = timestamp
        let progress = Math.min((timestamp - startTime) / duration, 1)
        this.$set(this.animatedBanks, id, from + (to - from) * progress)

        if (progress < 1) {
          requestAnimationFrame(step)
        } else {
          this.$set(this.animatedBanks, id, to)
        }
      }

      requestAnimationFrame(step)
    },
  },
  mounted: async function () {
    this.loading = true
    await this.loadPointTypes()
    await this.loadHabits()
    await this.loadHabitDays()
  },
}
</script>

<style scoped>
.tracker-frozen-row {
  display: flex;
  align-items: stretch;
  border-radius: 4px;
  overflow: hidden;
}

.tracker-frozen-table {
  flex: 0 0 auto;
  border-right: 1.5px solid #e0e0e0;
}

.tracker-frozen-cell {
  min-width: 108px;
  max-width: 150px;
  box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.15);
}

.tracker-frozen-line {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tracker-scroll-wrapper {
  flex: 1 1 auto;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  /* overflow-x: auto forces overflow-y to behave as auto too (per the CSS overflow
     spec), which would otherwise clip the floating popups/badges below since they're
     positioned above their cell. Padding-top reserves room for them inside this
     element's own scrollable box; the matching negative margin cancels it back out so
     the table's visual position on the page is unaffected. */
}

.tracker-scroll-wrapper > table,
.tracker-scroll-wrapper > div {
  min-width: 392px; /* Or whatever fixed width your table actually needs */
}

/* Both tables share these row-height classes so their rows line up exactly, since
   they're otherwise two independently-sized <table> elements. */
.tracker-header-row th {
  height: 48px;
  vertical-align: middle;
}

.tracker-body-row td {
  height: 64px !important;
  vertical-align: middle;
}

.tracker-day-col {
  min-width: 56px;
}

.tracker-cell {
  position: relative;
}

@media (max-width: 600px) {
  .tracker-day-col {
    padding: 4px 8px !important;
  }

  .tracker-frozen-cell {
    min-width: 92px;
    max-width: 110px;
    padding: 8px !important;
  }
}

.point-popup {
  position: absolute;
  top: 8px;
  left: 50%;
  font-weight: bold;
  font-size: 14px;
  pointer-events: none;
  animation: float-up-anim 900ms ease-out forwards;
}

@keyframes float-up-anim {
  0% {
    opacity: 0;
    transform: translate(-50%, 0);
  }
  15% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -40px);
  }
}

.bonus-badge {
  position: absolute;
  top: -4px;
  left: 50%;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 10px;
  border-radius: 8px;
  background-color: #fffbe6;
  border: 1px solid #ffc107;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  white-space: nowrap;
  pointer-events: none;
  animation: bonus-badge-anim 2200ms ease-out forwards;
}

.bonus-badge-icon {
  font-size: 26px !important;
}

.bonus-badge-text {
  font-weight: bold;
  font-size: 12px;
  color: #a15c00;
}

@keyframes bonus-badge-anim {
  0% {
    opacity: 0;
    transform: translate(-50%, 0) scale(0.4);
  }
  12% {
    opacity: 1;
    transform: translate(-50%, -6px) scale(1.15);
  }
  20% {
    transform: translate(-50%, -6px) scale(1);
  }
  70% {
    opacity: 1;
    transform: translate(-50%, -10px) scale(1);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -70px) scale(1);
  }
}
</style>
